import { useNotify, useRefresh } from "ra-core"
import React, { FormEvent, useState, useEffect } from "react"
import { Box, Grid, Modal } from "@mui/material"
import { UserRecord, updateUserLanguages, UserLanguages } from "api/users"
import RoundedPanel, { Label } from "common/RoundedPanel"
import { SumbitButtons } from "common/SubmitButtons"
import { LanguageMultiAutocomplete } from "common/LanguageFilter"
import { SelectOption } from "api/common"
import { getOptionsSelectFromArray } from "utils/helpers"
import { languages } from "common/dictonary"
import { handleEsc } from "common/closeOnEscape"
import "styles.scss"

interface EditLanguagesModalProps {
  record: UserRecord
  open: boolean
  onClose: () => void
  initialLanguages: UserLanguages | null
  onSave: () => void
}

export const EditLanguagesModal = ({ record, open, onClose, initialLanguages, onSave }: EditLanguagesModalProps) => {
  const refresh = useRefresh()
  const notify = useNotify()
  const allLanguageOptions = getOptionsSelectFromArray(languages, "dict.lang.")

  const [nativeLanguages, setNativeLanguages] = useState<SelectOption[]>([])
  const [mainLanguages, setMainLanguages] = useState<SelectOption[]>([])
  const [additionalLanguages, setAdditionalLanguages] = useState<SelectOption[]>([])

  useEffect(() => {
    if (open && initialLanguages) {
      const nativeOptions = initialLanguages.nativeLanguages
        .map((lang) => allLanguageOptions.find((opt) => opt.id === lang))
        .filter((opt): opt is SelectOption => opt !== undefined)
      const mainOptions = initialLanguages.mainLanguages
        .map((lang) => allLanguageOptions.find((opt) => opt.id === lang))
        .filter((opt): opt is SelectOption => opt !== undefined)
      const additionalOptions = initialLanguages.additionalLanguages
        .map((lang) => allLanguageOptions.find((opt) => opt.id === lang))
        .filter((opt): opt is SelectOption => opt !== undefined)

      setNativeLanguages(nativeOptions)
      setMainLanguages(mainOptions)
      setAdditionalLanguages(additionalOptions)
    } else if (!open) {
      // Reset state when modal closes
      setNativeLanguages([])
      setMainLanguages([])
      setAdditionalLanguages([])
    }
  }, [initialLanguages, open])

  React.useEffect(handleEsc(onClose), [])

  const handleSave = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const languagesData: UserLanguages = {
      nativeLanguages: nativeLanguages.map((opt) => opt.id.toLowerCase()),
      mainLanguages: mainLanguages.map((opt) => opt.id.toLowerCase()),
      additionalLanguages: additionalLanguages.map((opt) => opt.id.toLowerCase())
    }

    try {
      await updateUserLanguages(record.id, languagesData)
      refresh()
      onClose()
      onSave()
      notify("Languages updated successfully", { type: "success" })
    } catch (error) {
      console.error("Failed to update languages:", error)
      notify("Failed to update languages", { type: "warning" })
    }
  }

  return (
    <Modal open={open} onClose={onClose} disableAutoFocus={true}>
      <RoundedPanel modal>
        <h1>Edit Languages</h1>
        <Box height={32} />
        <form onSubmit={handleSave}>
          <Grid container spacing={"20px"}>
            <Grid item xs={12}>
              <LanguageMultiAutocomplete
                label="Native Languages"
                value={nativeLanguages}
                onChange={(newValue) => setNativeLanguages(newValue)}
              />
            </Grid>
            <Grid item xs={12}>
              <LanguageMultiAutocomplete
                label="Main Languages"
                value={mainLanguages}
                onChange={(newValue) => setMainLanguages(newValue)}
              />
            </Grid>
            <Grid item xs={12}>
              <LanguageMultiAutocomplete
                label="Additional Languages"
                value={additionalLanguages}
                onChange={(newValue) => setAdditionalLanguages(newValue)}
              />
            </Grid>
          </Grid>
          <Box height={16} />
          <hr />
          <SumbitButtons onClose={onClose} />
        </form>
      </RoundedPanel>
    </Modal>
  )
}
